import { Head, Link, router } from "@inertiajs/react";
import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TreeView } from "@/components/ui/tree-view";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Eye, Pencil, Plus, Trash2, ChevronsDownUp, ChevronsUpDown } from "lucide-react";

interface TreeNode {
  id: number;
  name: string;
  sort?: number | null;
  children?: TreeNode[];
  [key: string]: unknown;
}

interface Props {
  regions: TreeNode[];
}

export default function Index({ regions }: Props) {
  const [allExpanded, setAllExpanded] = React.useState(true);

  const handleDelete = (id: number) => {
    router.delete(`/desk/regions/${id}`);
  };

  const toggleAll = () => {
    setAllExpanded(!allExpanded);
  };

  const renderNode = (node: TreeNode) => {
    return (
      <div className="flex items-center justify-start w-full group">
        <div className="flex items-center gap-2 flex-1">
          <span className="font-medium">{node.name}</span>
          {node.sort !== null && node.sort !== undefined && (
            <span className="text-xs text-muted-foreground">(ترتيب: {node.sort})</span>
          )}
        </div>
        <div className="flex items-center gap-1 ms-auto opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="icon" asChild>
            <Link href={`/desk/regions/${node.id}`} onClick={(e) => e.stopPropagation()}>
              <Eye className="h-4 w-4" />
              <span className="sr-only">عرض</span>
            </Link>
          </Button>
          <Button variant="ghost" size="icon" asChild>
            <Link href={`/desk/regions/${node.id}/edit`} onClick={(e) => e.stopPropagation()}>
              <Pencil className="h-4 w-4" />
              <span className="sr-only">تعديل</span>
            </Link>
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-destructive"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">حذف</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>تأكيد الحذف</AlertDialogTitle>
                <AlertDialogDescription>
                  هل أنت متأكد من حذف هذه المنطقة؟ سيتم حذف جميع المناطق الفرعية أيضاً.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>إلغاء</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => handleDelete(node.id)}
                >
                  حذف
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    );
  };

  return (
    <>
      <Head title="المناطق" />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>المناطق</CardTitle>
              <CardDescription>إدارة قائمة المناطق وعرضها في شكل شجري</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={toggleAll}
                title={allExpanded ? "إغلاق الكل" : "فتح الكل"}
              >
                {allExpanded ? (
                  <ChevronsUpDown className="h-4 w-4" />
                ) : (
                  <ChevronsDownUp className="h-4 w-4" />
                )}
              </Button>
              <Button asChild>
                <Link href="/desk/regions/new">
                  <Plus className="ms-2 h-4 w-4" />
                  إضافة منطقة جديدة
                </Link>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <TreeView
            nodes={regions}
            renderNode={renderNode}
            defaultExpanded={true}
            expanded={allExpanded}
          />
        </CardContent>
      </Card>
    </>
  );
}
